/**
 * usePanelResize — shared resize-drag hook for right-edge-anchored, left-handle
 * slide-in drawers.
 *
 * Design notes:
 * - Width state is initialised once from localStorage (or `defaultWidth`), guarded
 *   via safeExecute so that a SecurityError or QuotaExceededError during the read
 *   never crashes the initial render.
 * - The resize effect depends ONLY on `[isResizing]`, not on `[isResizing, width]`.
 *   Depending on `width` would tear down and re-register document listeners on
 *   every pixel dragged, causing jank and stale-closure bugs. Instead, a ref
 *   (`widthRef`) shadows the state value so `handleMouseUp` always persists the
 *   final dragged width without needing `width` in the dep array.
 * - widthRef is initialised from `width` (the no-drag case) and written
 *   synchronously inside handleMouseMove so that handleMouseUp always reads the
 *   true final value regardless of React render/commit timing.  No passive
 *   useEffect is needed for the ref — the synchronous write in the handler
 *   supersedes it.
 * - localStorage is written only on mouseup (once per drag), never per pixel,
 *   and is guarded via safeExecute so a full-storage or private-browsing error
 *   never propagates to the caller.
 */

import {
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { safeExecute } from '@/utils/function.util';

export interface UsePanelResizeOptions {
  /** localStorage key — use a distinct key per drawer so widths don't collide. */
  storageKey: string;
  /** Minimum width in px. */
  minWidth: number;
  /** Maximum width in px. */
  maxWidth: number;
  /** Width in px used when no valid stored value exists. */
  defaultWidth: number;
}

export interface UsePanelResizeResult {
  /** Current drawer width in px. */
  width: number;
  /** True while the user is actively dragging the handle. */
  isResizing: boolean;
  /** Attach to the resize-handle element's onMouseDown. */
  onResizeHandleMouseDown: (e: ReactMouseEvent<Element>) => void;
}

export function usePanelResize(
  options: UsePanelResizeOptions
): UsePanelResizeResult {
  const { storageKey, minWidth, maxWidth, defaultWidth } = options;

  // Deliberate direct `localStorage` use (not `appStorage`): this is an
  // ephemeral, per-drawer UI preference with its own raw key — it has no need
  // for appStorage's tenant key-scoping, encryption, or cross-tab `clear()`
  // integration. Access is wrapped in `safeExecute` so a blocked-storage
  // SecurityError falls back to `defaultWidth` instead of crashing on mount.
  const [width, setWidth] = useState<number>(() => {
    const viewportCap =
      typeof window !== 'undefined' && window.innerWidth > 0
        ? window.innerWidth
        : maxWidth;
    const clamp = (w: number) => Math.max(minWidth, Math.min(w, viewportCap));
    const saved = safeExecute(() => localStorage.getItem(storageKey), null);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) {
        return clamp(parsed);
      }
    }
    return clamp(defaultWidth);
  });

  const [isResizing, setIsResizing] = useState(false);

  // Ref that mirrors `width` so the mouseup handler can read the latest value
  // without `width` being listed as an effect dependency.  Initialised from
  // `width` (the no-drag case) and kept current by the synchronous write
  // inside handleMouseMove, so mouseup always persists the true final value
  // independent of React render/commit timing.
  const widthRef = useRef(width);

  const onResizeHandleMouseDown = (e: ReactMouseEvent<Element>) => {
    // Suppress native text-selection drag-start.
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      const clamped = Math.min(maxWidth, Math.max(minWidth, newWidth));
      widthRef.current = clamped; // synchronous — mouseup reads the true final value, no commit-timing dependency
      setWidth(clamped);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      // Persist the final dragged width via ref — avoids stale closure.
      safeExecute(
        () => localStorage.setItem(storageKey, widthRef.current.toString()),
        undefined
      );
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isResizing, storageKey, minWidth, maxWidth]);

  return { width, isResizing, onResizeHandleMouseDown };
}
